import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch } from '../../lib/api';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './MonitoraggioAdozionePage.api';
import {
  RigaMonitoraggio, Feedback, UtenteRif, SocietaRif,
  TIPO_FEEDBACK_LABEL, fmtData, fmtDataOra, classeProgress, giorniAllaScadenza,
} from './adozione';

interface AttivitaScaduta {
  id: number;
  titolo: string;
  scadenza: string;
  stato: string;
  assegnatoA: UtenteRif;
  obiettivo: { id: number; titolo: string; piano: { id: number; societa: SocietaRif } };
}

interface Monitoraggio {
  righe: RigaMonitoraggio[];
  feedbackAperti: (Feedback & { piano: { id: number; titolo: string; societa: SocietaRif } })[];
  attivitaScadute: AttivitaScaduta[];
}

/**
 * Avanzamento dell'adozione società per società.
 *
 * Nessun grafico: con otto società e tre mesi una tabella ordinabile dice tutto
 * quello che serve, e un grafico sarebbe decorazione.
 */
export default function MonitoraggioAdozionePage() {
  const navigate = useNavigate();
  const { isSuperadmin } = usePermesso();

  const [dati, setDati] = useState<Monitoraggio | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [esitoRemind, setEsitoRemind] = useState('');

  const carica = useCallback(async () => {
    setIsLoading(true);
    setErrore('');
    try {
      // @api GET /api/adozione/monitoraggio -> {"success":true,"data":{"righe":[{"societaId":1,"societaNome":"Pianinvest","piano":{"id":1,"titolo":"Adozione BOP 2026","dataInizio":"2026-09-10","dataFine":"2026-12-10"},"totale":12,"chiuse":4,"percentuale":33,"obiettiviInRitardo":1,"attivitaInRitardo":2,"feedbackAperti":3,"ultimoMovimento":"2026-09-09T10:00:00.000Z"}],"feedbackAperti":[],"attivitaScadute":[]}}
      const res = await apiFetch<{ data: Monitoraggio }>('/api/adozione/monitoraggio');
      setDati(res.data);
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const lanciaRemind = async () => {
    setEsitoRemind('');
    try {
      // @api POST /api/adozione/remind/esegui -> {"success":true,"data":{"processati":4,"inviati":3,"saltati":1}}
      const res = await apiFetch<{ data: { processati: number; inviati: number; saltati: number } }>(
        '/api/adozione/remind/esegui', { method: 'POST' });
      setEsitoRemind(`Promemoria: ${res.data.inviati} inviati, ${res.data.saltati} saltati su ${res.data.processati} maturati.`);
    } catch (e) {
      setErrore((e as Error).message);
    }
  };

  const conPiano = dati?.righe.filter(r => r.piano) ?? [];
  const senzaPiano = dati?.righe.filter(r => !r.piano) ?? [];

  return (
    <Layout title="Monitoraggio gruppo">
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Monitoraggio gruppo</h1>
          <p className="bop-page-subtitle">
            Come sta procedendo l&apos;adozione di BOP, società per società.
          </p>
        </div>
        {isSuperadmin && (
          <div className="bop-flex">
            <button className="bop-btn bop-btn-secondary" onClick={lanciaRemind}
              title="Invia subito i promemoria già maturati, senza aspettare lo scheduler">
              Invia promemoria ora
            </button>
          </div>
        )}
      </div>

      {errore && <p className="bop-text-danger">{errore}</p>}
      {esitoRemind && <p className="bop-text-muted">{esitoRemind}</p>}
      {isLoading && <p className="bop-text-muted">Caricamento…</p>}

      {dati && (
        <>
          <div className="bop-card">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Società</th>
                  <th>Piano</th>
                  <th>Avanzamento</th>
                  <th>Attività</th>
                  <th>In ritardo</th>
                  <th>Feedback aperti</th>
                  <th>Ultimo movimento</th>
                </tr>
              </thead>
              <tbody>
                {conPiano.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="bop-empty-state">
                        <p className="bop-empty-state__text">Nessun piano di adozione attivo nel gruppo.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  conPiano.map(r => (
                    <tr key={r.societaId} onClick={() => navigate('/adozione/piano')}>
                      <td>{r.societaNome}</td>
                      <td className="bop-text-muted">
                        {r.piano!.titolo}
                        <br />
                        <span className="bop-text-muted">fino al {fmtData(r.piano!.dataFine)}</span>
                      </td>
                      <td>
                        <div className="bop-adozione__progress-riga">
                          <div className="bop-adozione__progress">
                            <div className={`bop-adozione__progress-bar ${classeProgress({
                              percentuale: r.percentuale,
                              inRitardo: r.attivitaInRitardo > 0,
                              stato: r.totale > 0 && r.chiuse === r.totale ? 'COMPLETATO' : 'IN_CORSO',
                            })}`} />
                          </div>
                          <span className="bop-adozione__progress-valore">{r.percentuale}%</span>
                        </div>
                      </td>
                      <td>{r.chiuse} / {r.totale}</td>
                      <td>
                        {r.attivitaInRitardo > 0 ? (
                          <span className="bop-badge bop-badge--warning">
                            {r.attivitaInRitardo} attività · {r.obiettiviInRitardo} obiettivi
                          </span>
                        ) : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>
                        {r.feedbackAperti > 0
                          ? <span className="bop-badge bop-badge--info">{r.feedbackAperti}</span>
                          : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>
                        <UltimoMovimento iso={r.ultimoMovimento} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {senzaPiano.length > 0 && (
            <div className="bop-card">
              <div className="bop-card__header">
                <h3 className="bop-card__title">Società senza piano attivo</h3>
              </div>
              <div className="bop-card__body">
                <p className="bop-text-muted">
                  Il rollout qui non è ancora partito: {senzaPiano.map(r => r.societaNome).join(', ')}.
                </p>
              </div>
            </div>
          )}

          <div className="bop-card">
            <div className="bop-card__header">
              <h3 className="bop-card__title">Feedback aperti più recenti</h3>
            </div>
            <div className="bop-card__body">
              {dati.feedbackAperti.length === 0 ? (
                <p className="bop-text-muted">Nessun feedback aperto.</p>
              ) : (
                <div className="bop-adozione__feedback">
                  {dati.feedbackAperti.map(f => (
                    <div className="bop-adozione__feedback-item" key={f.id}>
                      <div className="bop-adozione__feedback-head">
                        <span className={`bop-badge bop-badge--${TIPO_FEEDBACK_LABEL[f.tipo].variant}`}>
                          {TIPO_FEEDBACK_LABEL[f.tipo].label}
                        </span>
                        <span className="bop-adozione__feedback-autore">{f.autore.nome}</span>
                        <span>· {f.piano.societa.nome}</span>
                        <span>· {fmtDataOra(f.createdAt)}</span>
                      </div>
                      <p className="bop-adozione__feedback-testo">{f.testo}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bop-card">
            <div className="bop-card__header">
              <h3 className="bop-card__title">Attività scadute</h3>
            </div>
            <div className="bop-card__body">
              {dati.attivitaScadute.length === 0 ? (
                <p className="bop-text-muted">Nessuna attività scaduta.</p>
              ) : (
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Attività</th>
                      <th>Assegnata a</th>
                      <th>Società</th>
                      <th>Scaduta il</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dati.attivitaScadute.map(a => (
                      <tr key={a.id}>
                        <td>{a.titolo}</td>
                        <td>{a.assegnatoA.nome}</td>
                        <td className="bop-text-muted">{a.obiettivo.piano.societa.nome}</td>
                        <td className="bop-adozione__scadenza--ritardo">
                          {fmtData(a.scadenza)} ({-(giorniAllaScadenza(a.scadenza) ?? 0)} gg fa)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

/**
 * La colonna più diagnostica della pagina: una società ferma da tre settimane
 * si vede da qui prima che dalla percentuale.
 */
function UltimoMovimento({ iso }: { iso: string | null }) {
  if (!iso) return <span className="bop-text-muted">mai</span>;
  const giorni = -(giorniAllaScadenza(iso) ?? 0);
  const testo = giorni <= 0 ? 'oggi' : giorni === 1 ? 'ieri' : `${giorni} giorni fa`;
  const fermo = giorni >= 14;
  return (
    <span className={fermo ? 'bop-adozione__scadenza--ritardo' : undefined}>
      {testo}
    </span>
  );
}
